/**
 * User-facing remediation steps for M365 connection check rows from
 * m365GraphConnectionScriptRunner.parseM365GraphConnectionScriptOutput (check `id`).
 */

const GRANT_API_PERMISSIONS_BUTTON_FIX: readonly string[] = [
  'Click "Grant API Permissions" below to assign the required Microsoft Graph and Exchange permissions (including admin consent) for this app.',
];

const M365_CHECK_FIXES: Record<string, readonly string[]> = {
  m365_token: [
    "Confirm Tenant ID, Application (client) ID, and client secret match the Entra ID app registration you intend to use.",
    "If the secret may have expired, create a new client secret in Entra ID → App registrations → your app → Certificates & secrets, paste it in Client Secret above, then click Test. Theo saves the new secret after the credentials check succeeds.",
  ],
  m365_cert_entra: [
    "In Microsoft Entra admin center, open the app registration whose Application (client) ID matches your configuration.",
    "Under Certificates & secrets, upload the public certificate (.cer) that Theo Team sent you.",
  ],
  m365_required_graph_bundle: [
    "Go to Entra ID → App registrations → your app → API permissions → Add a permission → Microsoft Graph → Application permissions → add each permission named above → Grant admin consent for your organization.",
  ],
  m365_graph_3: GRANT_API_PERMISSIONS_BUTTON_FIX,
  m365_exchange_3: GRANT_API_PERMISSIONS_BUTTON_FIX,
  m365_sharepoint_3: GRANT_API_PERMISSIONS_BUTTON_FIX,
  // Directory roles are collapsed into one "Directory Roles (N)" bundle (m365_role_bundle);
  // the per-role ids are kept as fallbacks for any unbundled/legacy rendering.
  m365_role_bundle: GRANT_API_PERMISSIONS_BUTTON_FIX,
  m365_role_exchange_administrator: GRANT_API_PERMISSIONS_BUTTON_FIX,
  m365_role_compliance_administrator: GRANT_API_PERMISSIONS_BUTTON_FIX,
  m365_role_teams_administrator: GRANT_API_PERMISSIONS_BUTTON_FIX,
  m365_role_sharepoint_administrator: GRANT_API_PERMISSIONS_BUTTON_FIX,
};

export function getM365ConnectionCheckFixInstructions(
  checkId: string
): readonly string[] | null {
  const steps = M365_CHECK_FIXES[checkId];
  return steps?.length ? steps : null;
}

export function isM365CheckFailureStatus(status: string): boolean {
  const t = String(status || "").toLowerCase();
  return t !== "success" && t !== "skipped";
}

export type M365ConnectionTestCheck = {
  id?: string;
  status?: string;
};

export type M365ConnectionTestResult = {
  success?: boolean;
  checks?: M365ConnectionTestCheck[];
} | null | undefined;

/** True when Graph client-credentials token acquisition succeeded. */
export function didM365TokenCheckSucceed(
  result: M365ConnectionTestResult
): boolean {
  if (!result) return false;
  const tokenCheck = result.checks?.find((check) => check.id === "m365_token");
  if (tokenCheck) {
    return String(tokenCheck.status || "").toLowerCase() === "success";
  }
  return result.success === true;
}

/** True when the password field holds a new client secret (masked, so the UI needs a pill). */
export function isM365ClientSecretUpdated(
  previousSecret?: string | null,
  nextSecret?: string | null
): boolean {
  const next = String(nextSecret || "").trim();
  const previous = String(previousSecret || "").trim();
  return Boolean(next) && next !== previous;
}

/**
 * Persist a rotated M365 client secret only after the new value is present
 * and Graph accepted it. Empty or unchanged secrets are ignored.
 */
export function shouldPersistM365ClientSecret(opts: {
  previousSecret?: string | null;
  nextSecret?: string | null;
  testResult?: M365ConnectionTestResult;
}): boolean {
  if (!isM365ClientSecretUpdated(opts.previousSecret, opts.nextSecret)) {
    return false;
  }
  return didM365TokenCheckSucceed(opts.testResult);
}
